<template>
  <div>
    <b-card no-body class="mb-0">
      <div class="m-2 mail-toolbar">
        <div class="mail-toolbar__show">
          <label class="mb-0 text-nowrap">Show</label>
          <v-select
            v-model="perPage"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block mx-50"
          />
          <label class="mb-0 text-nowrap">entries</label>
        </div>

        <div class="mail-toolbar__controls">
          <b-form-input v-model="searchQuery" class="mail-toolbar__search" placeholder="Search mailbox..." />
          <v-select
            v-model="providerFilter"
            :options="providerOptions"
            :reduce="(o) => o.value"
            placeholder="All Providers"
            class="mail-toolbar__filter"
          />
          <v-select
            v-model="stateFilter"
            :options="stateOptions"
            :reduce="(o) => o.value"
            placeholder="All Statuses"
            class="mail-toolbar__filter"
          />
          <b-button variant="secondary" class="mail-toolbar__btn" @click="openInviteModal">
            <feather-icon icon="SendIcon" class="mr-50" />
            <span class="text-nowrap">Invite to Enroll</span>
          </b-button>
          <b-button variant="primary" class="mail-toolbar__btn" @click="openLinkModal">
            <feather-icon icon="PlusIcon" class="mr-50" />
            <span class="text-nowrap">Link Mailbox</span>
          </b-button>
        </div>
      </div>

      <b-table
        ref="refMailAccountsTable"
        class="position-relative"
        :items="fetchMailAccounts"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No mail accounts linked yet"
        :sort-desc.sync="isSortDirDesc"
      >
        <template #cell(email)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :variant="`light-${resolveStateVariant(data.item.state)}`"
                :text="(data.item.email || '?').charAt(0).toUpperCase()"
              />
            </template>
            <span class="font-weight-bold d-block text-nowrap">{{ data.item.email }}</span>
            <small
              class="text-muted d-block text-truncate"
              style="max-width: 220px"
              :title="data.item.engineAccountId"
            >
              {{ data.item.engineAccountId }}
            </small>
          </b-media>
        </template>

        <template #cell(name)="data">
          <span>{{ data.item.name || '—' }}</span>
        </template>

        <template #cell(provider)="data">
          <b-badge variant="light-info" class="text-capitalize">
            <feather-icon :icon="resolveProviderIcon(data.item.provider)" size="12" class="mr-25" />
            {{ data.item.provider }}
          </b-badge>
        </template>

        <template #cell(state)="data">
          <b-badge :variant="`light-${resolveStateVariant(data.item.state)}`" class="text-capitalize">
            {{ data.item.state }}
          </b-badge>
        </template>

        <template #cell(syncedAt)="data">
          <span v-if="data.item.syncedAt" class="text-nowrap">
            {{ moment(data.item.syncedAt).fromNow() }}
          </span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(lastError)="data">
          <span v-if="data.item.lastError && data.item.lastError.message" class="text-danger small">
            {{ data.item.lastError.message }}
          </span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(createdAt)="data">
          <span class="text-nowrap">{{ moment(data.value).format('L') }}</span>
        </template>

        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
            </template>

            <b-dropdown-item @click="syncAccount(data.item)">
              <feather-icon icon="RefreshCwIcon" />
              <span class="align-middle ml-50">Sync</span>
            </b-dropdown-item>

            <b-dropdown-item @click="confirmUnlink(data.item)">
              <feather-icon icon="TrashIcon" class="text-danger" />
              <span class="align-middle ml-50 text-danger">Unlink</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">
              Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries
            </span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalAccounts"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Pending Enrollment Invites -->
    <b-card v-if="invites.length || invitesLoading" no-body class="mt-2 mb-0">
      <div class="m-2 d-flex align-items-center justify-content-between">
        <h5 class="mb-0">Pending Enrollment Invites</h5>
        <b-button size="sm" variant="flat-secondary" :disabled="invitesLoading" @click="fetchInvites">
          <feather-icon icon="RefreshCwIcon" :class="{ spinner: invitesLoading }" />
        </b-button>
      </div>

      <b-table
        class="position-relative mb-0"
        :items="invites"
        responsive
        :fields="inviteColumns"
        primary-key="id"
        show-empty
        empty-text="No pending invites"
        :busy="invitesLoading"
      >
        <template #cell(name)="data">
          <span class="font-weight-bold">{{ data.item.name }}</span>
        </template>

        <template #cell(status)="data">
          <b-badge :variant="`light-${resolveInviteStatusVariant(data.item.status)}`" class="text-capitalize">
            {{ data.item.status }}
          </b-badge>
        </template>

        <template #cell(createdAt)="data">
          <span class="text-nowrap">{{ moment(data.item.createdAt).format('L') }}</span>
        </template>

        <template #cell(expiresAt)="data">
          <span v-if="data.item.expiresAt" class="text-nowrap">{{ moment(data.item.expiresAt).fromNow() }}</span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
            </template>

            <b-dropdown-item :disabled="data.item.status === 'accepted'" @click="resendInvite(data.item)">
              <feather-icon icon="SendIcon" />
              <span class="align-middle ml-50">Resend</span>
            </b-dropdown-item>

            <b-dropdown-item :disabled="data.item.status === 'accepted'" @click="confirmRevoke(data.item)">
              <feather-icon icon="XCircleIcon" class="text-danger" />
              <span class="align-middle ml-50 text-danger">Revoke</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </b-card>

    <!-- Invite to Enroll Modal -->
    <b-modal
      id="invite-enroll-modal"
      v-model="showInviteModal"
      title="Invite to Enroll a Mailbox"
      size="md"
      :ok-disabled="inviting || !inviteForm.name || !inviteForm.email"
      :ok-title="inviting ? 'Sending...' : 'Send Invite'"
      cancel-title="Cancel"
      @ok.prevent="submitInvite"
    >
      <b-form @submit.prevent="submitInvite">
        <b-form-group label="Full Name" label-for="invite-name">
          <b-form-input id="invite-name" v-model="inviteForm.name" placeholder="Jane Doe" required />
        </b-form-group>
        <b-form-group label="Email Address" label-for="invite-email">
          <b-form-input
            id="invite-email"
            v-model="inviteForm.email"
            type="email"
            placeholder="jane@example.com"
            required
          />
        </b-form-group>
        <b-form-group label="Attribute to Mgmt User (optional)" label-for="invite-owner">
          <v-select
            id="invite-owner"
            v-model="inviteForm.owner"
            :options="ownerOptions"
            :filterable="false"
            label="name"
            placeholder="Defaults to Apps - Email Archive"
            @search="onOwnerSearch"
          >
            <template #no-options>Type to search users…</template>
          </v-select>
        </b-form-group>
        <p class="text-muted small mb-0">
          We'll email <strong>{{ inviteForm.email || 'the recipient' }}</strong> a secure link to connect their
          <strong>Outlook</strong> mailbox. They don't need a Management account. The tracked mailbox is attributed to
          <strong>{{ inviteForm.owner ? inviteForm.owner.name : 'Apps - Email Archive' }}</strong
          >.
        </p>
      </b-form>
    </b-modal>

    <!-- Link Mailbox Modal -->
    <b-modal
      id="link-mailbox-modal"
      v-model="showLinkModal"
      title="Link a Mailbox"
      size="md"
      :ok-disabled="linking || !linkForm.email || !linkForm.provider"
      :ok-title="linking ? 'Requesting...' : 'Continue'"
      cancel-title="Cancel"
      @ok.prevent="submitLink"
    >
      <b-form @submit.prevent="submitLink">
        <b-form-group label="Provider" label-for="provider">
          <v-select
            id="provider"
            v-model="linkForm.provider"
            :options="linkProviderOptions"
            :reduce="(o) => o.value"
            :clearable="false"
          />
        </b-form-group>
        <b-form-group label="Email Address" label-for="email">
          <b-form-input
            id="email"
            v-model="linkForm.email"
            type="email"
            placeholder="you@intellectualpoint.com"
            required
          />
        </b-form-group>
        <b-form-group label="Display Name (optional)" label-for="name">
          <b-form-input id="name" v-model="linkForm.name" placeholder="How this mailbox appears" />
        </b-form-group>
        <p class="text-muted small mb-0">
          You'll be redirected to
          <strong class="text-capitalize">{{ linkForm.provider || 'the provider' }}</strong>
          to authorize access. After you approve, you'll land back here.
        </p>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import store from '@/store';
import { onUnmounted } from '@vue/composition-api';
import moment from 'moment';
import ToastificationContent from '@core/components/toastification/ToastificationContent';
import useMailAccountsList from './useMailAccountsList';
import mailAccountStoreModule from '../mailAccountStoreModule';

const STORE_MODULE_NAME = 'mailAccountStoreModule';

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    vSelect,
  },
  data: () => ({
    showLinkModal: false,
    linking: false,
    linkForm: { email: '', name: '', provider: 'outlook' },

    showInviteModal: false,
    inviting: false,
    inviteForm: { name: '', email: '', owner: null },
    ownerOptions: [],
    ownerSearchTimer: null,

    invites: [],
    invitesLoading: false,
    inviteColumns: [
      { key: 'name', sortable: false },
      { key: 'email', sortable: false },
      { key: 'status', sortable: false },
      { key: 'createdAt', label: 'Sent', sortable: false, thClass: 'text-nowrap' },
      { key: 'expiresAt', label: 'Expires', sortable: false, thClass: 'text-nowrap' },
      { key: 'actions', label: '', thClass: 'text-right', tdClass: 'text-right' },
    ],
  }),
  created() {
    this.fetchInvites();
  },
  methods: {
    // --- Enrollment invites ---
    openInviteModal() {
      this.inviteForm = { name: '', email: '', owner: null };
      this.ownerOptions = [];
      this.showInviteModal = true;
    },
    onOwnerSearch(search, loading) {
      if (!search || search.length < 2) return;
      loading(true);
      clearTimeout(this.ownerSearchTimer);
      this.ownerSearchTimer = setTimeout(() => {
        this.$http
          .get('v1/users', { params: { search, limit: 20 } })
          .then((response) => {
            this.ownerOptions = (response.data.results || []).map((u) => ({ id: u.id, name: u.name, email: u.email }));
          })
          .catch(() => {
            this.ownerOptions = [];
          })
          .finally(() => loading(false));
      }, 300);
    },
    submitInvite() {
      this.inviting = true;
      this.$store
        .dispatch(`${STORE_MODULE_NAME}/createInvite`, {
          name: this.inviteForm.name,
          email: this.inviteForm.email,
          ownerUserId: this.inviteForm.owner ? this.inviteForm.owner.id : undefined,
        })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Invite sent',
              icon: 'SendIcon',
              variant: 'success',
              text: `An enrollment link was emailed to ${this.inviteForm.email}.`,
            },
          });
          this.showInviteModal = false;
          this.fetchInvites();
        })
        .catch((err) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Failed to send invite',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: err?.response?.data?.message || err.message,
            },
          });
        })
        .finally(() => {
          this.inviting = false;
        });
    },
    fetchInvites() {
      this.invitesLoading = true;
      this.$store
        .dispatch(`${STORE_MODULE_NAME}/fetchInvites`, {
          status: 'pending',
          sortBy: 'createdAt:desc',
          limit: 100,
        })
        .then((response) => {
          this.invites = response.data.results || [];
        })
        .catch(() => {
          this.invites = [];
        })
        .finally(() => {
          this.invitesLoading = false;
        });
    },
    resendInvite(item) {
      this.$store
        .dispatch(`${STORE_MODULE_NAME}/resendInvite`, { id: item.id })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: { title: 'Invite re-sent', icon: 'SendIcon', variant: 'success' },
          });
          this.fetchInvites();
        })
        .catch((err) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Resend failed',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: err?.response?.data?.message || err.message,
            },
          });
        });
    },
    async confirmRevoke(item) {
      const ok = await this.$bvModal.msgBoxConfirm(`Revoke the enrollment invite for ${item.email}?`, {
        title: 'Revoke Invite',
        okVariant: 'danger',
        okTitle: 'Revoke',
        cancelTitle: 'Cancel',
        centered: true,
      });
      if (!ok) return;

      this.$store
        .dispatch(`${STORE_MODULE_NAME}/revokeInvite`, { id: item.id })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: { title: 'Invite revoked', icon: 'CheckIcon', variant: 'success' },
          });
          this.fetchInvites();
        })
        .catch((err) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Revoke failed',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: err?.response?.data?.message || err.message,
            },
          });
        });
    },
    resolveInviteStatusVariant(status) {
      if (status === 'accepted') return 'success';
      if (status === 'pending') return 'warning';
      if (status === 'expired') return 'danger';
      return 'secondary';
    },
    openLinkModal() {
      this.linkForm = { email: this.$store.state.auth?.user?.email || '', name: '', provider: 'outlook' };
      this.showLinkModal = true;
    },
    submitLink() {
      this.linking = true;
      this.$store
        .dispatch(`${STORE_MODULE_NAME}/createMailAccount`, { ...this.linkForm })
        .then((response) => {
          const { url } = response.data || {};
          if (url) {
            window.location.href = url;
            return;
          }
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Link initiated',
              icon: 'CheckIcon',
              variant: 'success',
              text: 'The provider did not return a redirect URL. Check mail-engine logs.',
            },
          });
          this.showLinkModal = false;
          this.refetchData();
        })
        .catch((err) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Failed to link mailbox',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: err?.response?.data?.message || err.message,
            },
          });
        })
        .finally(() => {
          this.linking = false;
        });
    },
    syncAccount(item) {
      this.$store
        .dispatch(`${STORE_MODULE_NAME}/syncMailAccount`, { id: item.id })
        .then(() => {
          this.refetchData();
          this.$toast({
            component: ToastificationContent,
            props: { title: 'Synced', icon: 'CheckIcon', variant: 'success' },
          });
        })
        .catch((err) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Sync failed',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: err?.response?.data?.message || err.message,
            },
          });
        });
    },
    async confirmUnlink(item) {
      const ok = await this.$bvModal.msgBoxConfirm(`Unlink ${item.email}? This removes the mailbox from EmailEngine.`, {
        title: 'Unlink Mailbox',
        okVariant: 'danger',
        okTitle: 'Unlink',
        cancelTitle: 'Cancel',
        centered: true,
      });
      if (!ok) return;

      this.$store
        .dispatch(`${STORE_MODULE_NAME}/deleteMailAccount`, { id: item.id })
        .then(() => {
          this.refetchData();
          this.$toast({
            component: ToastificationContent,
            props: { title: 'Unlinked', icon: 'CheckIcon', variant: 'success' },
          });
        })
        .catch((err) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Unlink failed',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: err?.response?.data?.message || err.message,
            },
          });
        });
    },
  },
  setup() {
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, mailAccountStoreModule);
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME);
    });

    const providerOptions = [
      { label: 'All Providers', value: null },
      { label: 'Gmail', value: 'gmail' },
      { label: 'Outlook', value: 'outlook' },
      { label: 'IMAP', value: 'imap' },
      { label: 'Other', value: 'other' },
    ];

    const linkProviderOptions = [
      { label: 'Microsoft 365 / Outlook', value: 'outlook' },
      { label: 'Gmail', value: 'gmail' },
      { label: 'IMAP / SMTP', value: 'imap' },
    ];

    const stateOptions = [
      { label: 'All Statuses', value: null },
      { label: 'Connected', value: 'connected' },
      { label: 'Connecting', value: 'connecting' },
      { label: 'Auth Error', value: 'authenticationError' },
      { label: 'Connect Error', value: 'connectError' },
      { label: 'Disconnected', value: 'disconnected' },
    ];

    const {
      fetchMailAccounts,
      tableColumns,
      perPage,
      currentPage,
      totalAccounts,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refMailAccountsTable,
      refetchData,

      providerFilter,
      stateFilter,

      resolveStateVariant,
      resolveProviderIcon,
    } = useMailAccountsList();

    return {
      fetchMailAccounts,
      tableColumns,
      perPage,
      currentPage,
      totalAccounts,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refMailAccountsTable,
      refetchData,

      providerFilter,
      stateFilter,

      providerOptions,
      linkProviderOptions,
      stateOptions,

      resolveStateVariant,
      resolveProviderIcon,

      moment,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

// Responsive toolbar: inline on wide screens, cleanly stacked on narrow ones.
.mail-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;

  &__show {
    display: flex;
    align-items: center;
  }

  &__controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    margin-left: auto; // push filters/actions to the right when there's room
  }

  &__search {
    width: 240px;
  }

  &__filter {
    min-width: 170px;
  }
}

// Below the point where everything fits on one line, stack cleanly: search spans the
// full width, then the two filters share a row, then the two buttons share a row.
// `calc(50% - halfGap)` forces exactly two items per row so nothing gets crushed.
@media (max-width: 1199.98px) {
  .mail-toolbar__controls {
    margin-left: 0;
    width: 100%;
  }
  .mail-toolbar__search {
    flex: 1 1 100%;
    width: auto;
  }
  .mail-toolbar__filter {
    flex: 1 1 calc(50% - 0.375rem);
    min-width: 0;
  }
  .mail-toolbar__btn {
    flex: 1 1 calc(50% - 0.375rem);
  }
}

// Phones: one control per row is cleaner than cramped halves.
@media (max-width: 575.98px) {
  .mail-toolbar__show {
    width: 100%;
  }
  .mail-toolbar__filter,
  .mail-toolbar__btn {
    flex: 1 1 100%;
  }
}
.spinner {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
